/**
 * Kanaalbepaling van een reno-mail: leverancier (RS) of huurder (RT).
 *
 * De huurder/leverancier-schakelaar op de ticketdetailpagina moet enkel de
 * correspondentie van het gekozen kanaal tonen. Zonder deze bepaling stond een
 * leveranciersmail (onderwerp met RS-code) ook onder het huurdergedeelte.
 *
 * Volgorde van de bronnen — dezelfde regel als backend-side in
 * `RenoHelpdeskService.loadEmailChannelCounts`, zodat de tellers in de lijst en
 * de mails op de detailpagina niet uit elkaar kunnen lopen:
 *
 * 1. `metadata.channel` — gezet bij het verzenden en bij inkomende mail met code;
 * 2. de RS-/RT-code in het onderwerp — dekt oudere mails van vóór stap 1;
 * 3. anders `null`: niet toewijsbaar.
 *
 * Een niet-toewijsbare mail (bv. de inkomende mail die het ticket aanmaakte, of
 * een antwoord waarin de code uit het onderwerp gewist is) blijft in **beide**
 * kanalen zichtbaar. Losse correspondentie mag niet stil verdwijnen doordat we
 * ze niet kunnen plaatsen.
 */

export type RenoEmailChannel = 'supplier' | 'tenant';

/** Minimale vorm die nodig is om het kanaal te bepalen. */
export interface RenoChannelEmail {
  subject?: string | null;
  metadata?: { channel?: string | null } | null;
}

const SUPPLIER_CODE = /\bRS\s*-?\s*\d+/i;
const TENANT_CODE = /\bRT\s*-?\s*\d+/i;

/** Het kanaal van een mail, of `null` als het niet te bepalen is. */
export const resolveRenoEmailChannel = (
  email: RenoChannelEmail | null | undefined,
): RenoEmailChannel | null => {
  const stored = email?.metadata?.channel;
  if (stored === 'supplier' || stored === 'tenant') return stored;

  const subject = email?.subject ?? '';
  if (SUPPLIER_CODE.test(subject)) return 'supplier';
  if (TENANT_CODE.test(subject)) return 'tenant';
  return null;
};

/** true wanneer de mail in dit kanaal hoort (niet-toewijsbare mails horen in beide). */
export const renoEmailMatchesChannel = (
  email: RenoChannelEmail | null | undefined,
  channel: RenoEmailChannel,
): boolean => {
  const resolved = resolveRenoEmailChannel(email);
  return resolved === null || resolved === channel;
};

/** De mails van één kanaal (niet-toewijsbare mails komen in beide lijsten terug). */
export const filterRenoEmailsByChannel = <T extends RenoChannelEmail>(
  emails: T[],
  channel: RenoEmailChannel,
): T[] => emails.filter((email) => renoEmailMatchesChannel(email, channel));
